import React, { useState, useEffect } from 'react';
import { HeartOutlined, HeartFilled, ShoppingCartOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import './ProductCard.css';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';

const ProductCard = ({ product, onWishlistToggle }) => {
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  // Get the correct image URL from product data
  const getProductImage = () => {
    if (product.image_fullpath && product.image_fullpath.length > 0) {
      return product.image_fullpath[0];
    }
    if (product.image_fullpath && typeof product.image_fullpath === 'string') {
      return product.image_fullpath;
    }
    if (product.image && Array.isArray(product.image) && product.image.length > 0) {
      return product.image[0];
    }
    if (product.image && typeof product.image === 'string') {
      return product.image;
    }
    return 'https://placehold.co/400x300?text=Product+Image';
  };

  useEffect(() => {
    setIsWishlisted(isInWishlist(product.id));
  }, [product.id, isInWishlist]);

  const truncateDescription = (desc, maxLength = 25) => {
    if (!desc) return '';
    return desc.length > maxLength ? desc.substring(0, maxLength) + '...' : desc;
  };

  const handleWishlistClick = async () => {
    setWishlistLoading(true);
    try {
      if (isWishlisted) {
        const success = await removeFromWishlist(product.id);
        if (success) {
          setIsWishlisted(false);
          if (onWishlistToggle) {
            onWishlistToggle(product);
          }
        }
      } else {
        const success = await addToWishlist(product);
        if (success) {
          setIsWishlisted(true);
          if (onWishlistToggle) {
            onWishlistToggle(product);
          }
        }
      }
    } finally {
      setWishlistLoading(false);
    }
  };

  const handleAddToCart = () => {
    // Extract numeric price from formatted price string if needed
    let numericPrice = product.price;
    if (typeof product.price === 'string') {
      const cleaned = product.price.replace(/[KSh$\s,]/g, '');
      numericPrice = parseFloat(cleaned) || 0;
    }
    
    addToCart({
      ...product,
      price: numericPrice
    });
  };

  const handleCardNavigate = (e) => {
    // Don't navigate when interacting with buttons/links inside the card
    const target = e.target;
    if (target && typeof target.closest === 'function') {
      if (target.closest('button') || target.closest('a')) return;
    }
    navigate(`/product/${product.id}`);
  };

  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return price.toFixed(0);
    }
    if (typeof price === 'string') {
      const cleaned = price.replace(/[KSh$\s,]/g, '');
      const numeric = parseFloat(cleaned);
      return isNaN(numeric) ? '0' : numeric.toFixed(0);
    }
    return '0';
  };

  return (
    <div
      className="product-card"
      role="link"
      tabIndex={0}
      onClick={handleCardNavigate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardNavigate(e);
        }
      }}
    >
      <div className="product-card-header">
        {product.discount > 0 && (
          <div className="discount-badge">{product.discount}% off</div>
        )}
      </div>
      <div className="product-card-wishlist">
        <button 
          className={`wishlist-btn ${isWishlisted ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleWishlistClick();
          }}
          disabled={wishlistLoading}
        >
          {wishlistLoading ? (
            <span className="wishlist-loading-spinner">...</span>
          ) : (
            isWishlisted ? <HeartFilled /> : <HeartOutlined />
          )}
        </button>
      </div>
      
      <Link to={`/product/${product.id}`} className="product-image-link">
        <div className="product-image-container">
          <img 
            src={getProductImage()} 
            alt={`${product.name} `}
            className="product-image"
            onLoad={(e) => {
            }}
            onError={(e) => {
              e.target.src = 'https://placehold.co/400x300?text=Product+Image';
            }}
          />
        </div>
      </Link>
      
      <div className="product-info">
        <h4 className="product-name">{product.name}</h4>
        <p className="product-description">{truncateDescription(product.description)}</p>
        <div className="product-footer">
          <div className="price-container">
            {product.discount > 0 ? (
              <>
                <span className="original-price">Ksh.{formatPrice(product.originalPrice)}</span>
                <span className="product-price discounted">Ksh.{formatPrice(product.price)}</span>
              </>
            ) : (
              <span className="product-price">Ksh.{formatPrice(product.price)}</span>
            )}
          </div>
          <button
            className="add-to-cart-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart();
            }}
          > 
            ADD TO CART
           <ShoppingCartOutlined />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
